import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button, Input } from "../lib/ui.js";
import { normalizeAuPhone } from "../lib/phone.js";
import { BookingApiError, type BookingApi, type Purpose } from "./api.js";
import { errorMessage } from "./helpers.js";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: { sitekey: string; callback: (token: string) => void; "expired-callback": () => void }) => string;
      reset: (id?: string) => void;
    };
  }
}

/** Cloudflare Turnstile, only when the platform has switched it on (the function reports a site key). A token works once, so it is reset after each send. */
function useTurnstile(siteKey: string | null) {
  const ref = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!siteKey) return;
    const render = () => {
      if (ref.current && window.turnstile && widget.current === null) {
        widget.current = window.turnstile.render(ref.current, { sitekey: siteKey, callback: setToken, "expired-callback": () => setToken(null) });
      }
    };
    if (window.turnstile) {
      render();
      return;
    }
    let script = document.querySelector<HTMLScriptElement>("script[data-turnstile]");
    if (!script) {
      script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.dataset.turnstile = "1";
      document.head.appendChild(script);
    }
    script.addEventListener("load", render);
    return () => script?.removeEventListener("load", render);
  }, [siteKey]);

  const reset = () => {
    setToken(null);
    if (widget.current && window.turnstile) window.turnstile.reset(widget.current);
  };
  return { ref, token, reset };
}

export interface Verified {
  token: string;
  phone: string;
  needsName: boolean;
  name: string | null;
  dobMode: "off" | "verify" | "collect";
}

const RESEND_SECONDS = 30;
const SEE_BOOKINGS_NOTE = "You will see your current bookings once you enter the SMS code.";
const PHONE_NOTE = "If you do not receive the SMS code within 3 minutes, you can book by phone. The phone number is at the top of this page.";

/**
 * Mobile number, then the six-digit code texted to it. The page learns nothing about the number until the code is right: an existing
 * customer is greeted by name, a new one is asked for theirs (by the caller of this step).
 */
export function VerifyStep({
  api,
  purpose,
  siteKey,
  onVerified,
  message,
  plain = false,
  hasPhone = false,
}: {
  api: BookingApi;
  purpose: Purpose;
  siteKey: string | null;
  onVerified: (v: Verified) => void;
  /** Something to say first (a session that ended). */
  message?: string | null;
  /** Draw no card of its own (it sits inside another panel). */
  plain?: boolean;
  /** The business has a phone number on the page header: say customers can ring instead. */
  hasPhone?: boolean;
}) {
  const [stage, setStage] = useState<"phone" | "code">("phone");
  const [rawPhone, setRawPhone] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const turnstile = useTurnstile(siteKey);

  useEffect(() => {
    if (stage !== "code") return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [stage]);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    const n = normalizeAuPhone(rawPhone, null);
    if (!n.ok || !/^\+614\d{8}$/.test(n.e164)) {
      setError(errorMessage("not_a_mobile"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.sendCode(n.e164, purpose, turnstile.token);
      setPhone(n.e164);
      setCode("");
      setStage("code");
      setResendAt(Date.now() + RESEND_SECONDS * 1000);
      setNow(Date.now());
    } catch (err) {
      setError(errorMessage(err instanceof BookingApiError ? err.code : "unexpected_error"));
    } finally {
      turnstile.reset();
      setBusy(false);
    }
  }

  async function check(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.verify(phone, purpose, code);
      onVerified({ token: r.token, phone, needsName: r.needs_name, name: r.name, dobMode: r.dob_mode ?? "off" });
    } catch (err) {
      setError(err instanceof BookingApiError ? errorMessage(err.code, err.detail, err.attemptsLeft) : errorMessage("unexpected_error"));
    } finally {
      setBusy(false);
    }
  }

  const secondsToResend = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const needsBotCheck = !!siteKey && !turnstile.token;

  return (
    <div className={plain ? undefined : "bk-card"}>
      {message && <p className="bk-error" role="alert" style={{ marginTop: 0, marginBottom: 12 }}>{message}</p>}
      {stage === "phone" ? (
        <form onSubmit={send} className="bk-stack" style={{ gap: 12 }}>
          <p className="bk-muted" style={{ margin: 0 }}>
            {purpose === "book"
              ? "We will text a code to your mobile to check it is you. You will not need another one for 20 minutes on this device."
              : "We will text a code to the mobile number the booking was made with. You will need a new code for every change."}
          </p>
          <label className="bk-label">
            Your mobile number
            <Input type="tel" inputMode="tel" autoComplete="tel" value={rawPhone} onChange={(e) => setRawPhone(e.target.value)} required />
          </label>
          <Button type="submit" variant="primary" disabled={busy || needsBotCheck || !rawPhone.trim()}>
            {busy ? "Sending..." : "Text me a code"}
          </Button>
          <p className="bk-phonenote">{SEE_BOOKINGS_NOTE}</p>
        </form>
      ) : (
        <form onSubmit={check} className="bk-stack" style={{ gap: 12 }}>
          <p className="bk-muted" style={{ margin: 0 }}>We texted a 6-digit code to your mobile. It lasts 10 minutes.</p>
          <label className="bk-label">
            Code
            <Input
              className="bk-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              aria-label="6-digit code"
              required
              autoFocus
            />
          </label>
          <Button type="submit" variant="primary" disabled={busy || code.replace(/\D/g, "").length !== 6}>
            {busy ? "Checking..." : "Check code"}
          </Button>
          <div className="bk-row bk-muted">
            <button type="button" className="bk-link" onClick={() => { setStage("phone"); setError(null); }}>Use a different number</button>
            <span aria-hidden="true">.</span>
            {secondsToResend > 0 ? (
              <span>Send again in {secondsToResend}s</span>
            ) : (
              <button type="button" className="bk-link" disabled={busy || needsBotCheck} onClick={() => void send()}>Send the code again</button>
            )}
          </div>
          {hasPhone && <p className="bk-phonenote">{PHONE_NOTE}</p>}
        </form>
      )}
      {/* Always mounted (the widget is created once); only shown when a code is about to be asked for. */}
      {siteKey && <div ref={turnstile.ref} style={{ marginTop: 12, display: stage === "phone" || secondsToResend === 0 ? "block" : "none" }} />}
      {error && <p className="bk-error" role="alert">{error}</p>}
    </div>
  );
}
